import { requestIdleCallback } from '@theme/utilities';

const API_VERSION = '2025-01';
const MAX_COMPONENTS = 30;
const FETCH_DEBOUNCE_MS = 50;

/** @type {Set<BundleProductCount>} */
const pendingElements = new Set();

/** @type {Map<string, number | null>} */
const countCache = new Map();

/** @type {number | null} */
let fetchTimer = null;

/**
 * @returns {string | undefined}
 */
function getStorefrontAccessToken() {
  const featuresScript = document.getElementById('shopify-features');
  if (!(featuresScript instanceof HTMLScriptElement)) return undefined;

  try {
    const features = JSON.parse(featuresScript.textContent || '{}');
    return typeof features.accessToken === 'string' ? features.accessToken : undefined;
  } catch {
    return undefined;
  }
}

/**
 * @param {number} count
 * @returns {string}
 */
function formatBundleCount(count) {
  const template = window.Theme?.translations?.bundle_product_count_label;
  if (typeof template === 'string' && template.includes('%count%')) {
    return template.replace('%count%', String(count));
  }

  return count === 1 ? `${count} product` : `${count} products`;
}

/**
 * @param {string[]} handles
 * @returns {Promise<Map<string, number>>}
 */
async function fetchBundleCounts(handles) {
  const token = getStorefrontAccessToken();
  const shop = window.Shopify?.shop;
  const counts = new Map();

  if (!token || !shop || handles.length === 0) return counts;

  const uncachedHandles = handles.filter((handle) => !countCache.has(handle));
  if (uncachedHandles.length === 0) {
    for (const handle of handles) {
      const cached = countCache.get(handle);
      if (typeof cached === 'number' && cached > 0) counts.set(handle, cached);
    }
    return counts;
  }

  const aliases = uncachedHandles
    .map((handle, index) => {
      const safeHandle = handle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `
        p${index}: product(handle: "${safeHandle}") {
          variants(first: 1) {
            nodes {
              requiresComponents
              components(first: ${MAX_COMPONENTS}) {
                nodes {
                  quantity
                }
              }
            }
          }
        }
      `;
    })
    .join('\n');

  const response = await fetch(`https://${shop}/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token,
    },
    body: JSON.stringify({ query: `{${aliases}}` }),
  });

  if (!response.ok) return counts;

  const payload = await response.json();
  const data = payload?.data;
  if (!data || typeof data !== 'object') return counts;

  uncachedHandles.forEach((handle, index) => {
    const product = data[`p${index}`];
    const variant = product?.variants?.nodes?.[0];
    const componentNodes = variant?.requiresComponents ? variant?.components?.nodes : [];

    const count = Array.isArray(componentNodes) ? componentNodes.length : 0;
    countCache.set(handle, count > 0 ? count : null);

    if (count > 0) counts.set(handle, count);
  });

  return counts;
}

function scheduleFetch() {
  if (fetchTimer != null) window.clearTimeout(fetchTimer);

  fetchTimer = window.setTimeout(() => {
    fetchTimer = null;
  void resolvePendingElements();
  }, FETCH_DEBOUNCE_MS);
}

async function resolvePendingElements() {
  const elements = [...pendingElements];
  pendingElements.clear();

  if (elements.length === 0) return;

  const handles = [...new Set(elements.map((element) => element.productHandle).filter(Boolean))];

  try {
    const counts = await fetchBundleCounts(handles);

    for (const element of elements) {
      const handle = element.productHandle;
      if (!handle) continue;

      const count = counts.get(handle);
      if (typeof count === 'number' && count > 0) {
        element.applyCount(count);
      } else if (!element.hasLiquidCount) {
        element.hide();
      }
    }
  } catch {
    for (const element of elements) {
      if (!element.hasLiquidCount) element.hide();
    }
  }
}

class BundleProductCount extends HTMLElement {
  static observedAttributes = ['data-product-handle'];

  /** @type {MutationObserver | null} */
  static #domObserver = null;

  static {
    requestIdleCallback(() => {
      document.querySelectorAll('bundle-product-count[data-product-handle]').forEach((element) => {
        if (element instanceof BundleProductCount) element.#register();
      });

      BundleProductCount.#domObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;

            if (node.matches('bundle-product-count[data-product-handle]') && node instanceof BundleProductCount) {
              node.#register();
            }

            node.querySelectorAll?.('bundle-product-count[data-product-handle]').forEach((element) => {
              if (element instanceof BundleProductCount) element.#register();
            });
          });
        }
      });

      BundleProductCount.#domObserver.observe(document.body, { childList: true, subtree: true });
    });
  }

  connectedCallback() {
    this.#register();
  }

  disconnectedCallback() {
    pendingElements.delete(this);
  }

  get productHandle() {
    return this.dataset.productHandle || '';
  }

  get hasLiquidCount() {
    return this.dataset.liquidCount != null && this.dataset.liquidCount !== '';
  }

  #register() {
    if (!this.productHandle) return;

    if (this.hasLiquidCount) {
      this.hidden = false;
      return;
    }

    pendingElements.add(this);
    scheduleFetch();
  }

  /**
   * @param {number} count
   */
  applyCount(count) {
    this.textContent = formatBundleCount(count);
    this.hidden = false;
    this.dataset.bundleCount = String(count);
  }

  hide() {
    this.hidden = true;
    this.textContent = '';
  }
}

if (!customElements.get('bundle-product-count')) {
  customElements.define('bundle-product-count', BundleProductCount);
}
